import type { LensCalendar } from '~/types/leLens';

import { LENS_DEFAULT_CALENDAR } from './lensCalendar';
import { SUB_DAY_UNITS, TIME_UNITS, type TimeUnit, bucketText, instantOf, truncate } from './lensTime';

export { parseGrain } from './lensTime';

/**
 * Coarser time buckets a card can group a shared dataset by.
 *
 * Switching a card from daily to monthly used to mean asking AskRichard to
 * rewrite its SQL, which is several turns and a query that no longer matches
 * the one beside it. A dataset returns days; the grain buckets them where the
 * card already reduces the rows, so the same dataset serves a daily card and a
 * monthly one without a second query.
 */
export const LENS_GRAINS = TIME_UNITS;

export type LensGrain = TimeUnit;

/**
 * A grain reads as a column name because that is what it stands in for: the
 * card's Group by holds it, the backend stores it as the string it is, and
 * nothing has to learn a new field.
 */
export function grainExpression(grain: LensGrain, column: string): string {
  return `${grain}(${column})`;
}

/** The grain choices a result set can offer, one set per date column it carries. */
export function grainOptions(rows: Array<Record<string, unknown>>, columns: Array<string>): Array<string> {
  return columns
    .filter((column) => rows.some((row) => instantOf(row[column], LENS_DEFAULT_CALENDAR, column) !== undefined))
    .flatMap((column) =>
      LENS_GRAINS.filter(
        (grain) => !SUB_DAY_UNITS.includes(grain) || rows.some((row) => carriesClockTime(row[column])),
      ).map((grain) => grainExpression(grain, column)),
    );
}

export function grainsForType(type: string | undefined): ReadonlyArray<LensGrain> {
  return type?.toUpperCase() === 'DATE' ? LENS_GRAINS.filter((grain) => !SUB_DAY_UNITS.includes(grain)) : LENS_GRAINS;
}

function carriesClockTime(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 10 && instantOf(value) !== undefined;
}

/**
 * The bucket a value falls in, as the first day of that bucket.
 *
 * Returned as a plain YYYY-MM-DD so it stays date-like: the axis keeps
 * formatting it as a date rather than dropping to a category of strings.
 */
export function bucketOf(
  value: unknown,
  grain: LensGrain,
  calendar: LensCalendar = LENS_DEFAULT_CALENDAR,
  column = '',
): string | undefined {
  const instant = instantOf(value, calendar, column);
  return instant === undefined ? undefined : bucketText(truncate(instant, grain, calendar), grain, calendar);
}
